import 'reflect-metadata'

import { DateFormat } from '@agroone/dates'
import { IsDateValidate, IsISO8601WithTimezone, RequireProperty } from '@agroone/helpers'
import { plainToInstance, Type } from 'class-transformer'
import {
  ArrayMaxSize,
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Max,
  MaxLength,
  Min,
  Validate,
  ValidateNested,
} from 'class-validator'
import { SaveBoundary } from './save-boundary'
import { SaveLocation } from './save-location'
import { SaveReporting } from './save-reporting'

// CONDITIONS
export class SaveScoutingEnvironment {
  @IsNotEmpty()
  @IsString()
  item!: string

  @IsString()
  @IsNotEmpty()
  cropVigor!: string

  @IsBoolean()
  @IsNotEmpty()
  cropConsistent!: boolean
}

export class SaveScoutingWeatherDamage {
  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  item!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  severity!: string
}

export class SaveScoutingFlowering {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsNotEmpty()
  @IsString()
  @IsDateValidate(DateFormat.YYYY_MM_DD)
  date!: string

  @IsNumber()
  @Type(() => Number)
  @IsInt()
  @Min(0)
  @Max(100)
  percentage!: number
}

export class SaveScoutingFoodSafety {
  @IsBoolean()
  hasAnimalIntrusion!: boolean
}

// MATURITY
export class SaveScoutingGrowthStage {
  @IsString()
  @IsNotEmpty()
  @MaxLength(90)
  item!: string

  @IsNumber()
  @IsOptional()
  density?: number
}

export class SaveScoutingYieldStandCount {
  @IsNumber()
  @IsOptional()
  id?: number

  @IsNotEmpty()
  @IsInt()
  @Min(0)
  headCount!: number

  @IsNotEmpty()
  @IsInt()
  @Min(0)
  standCount!: number

  @IsNotEmpty()
  @IsNumber()
  @Min(0)
  @RequireProperty('weightUnit')
  weight!: number

  @IsNotEmpty()
  @IsString()
  @RequireProperty('weight')
  weightUnit!: string
}

export class SaveScoutingYield {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsOptional()
  @Validate(IsISO8601WithTimezone)
  forecastHarvestDate?: string

  @IsOptional()
  @IsNumber()
  @Min(0.01)
  value?: number

  @IsOptional()
  @IsString()
  @IsIn(['kg/ha', 'kg/m2', 'lb/ac', 'mt/ac', 'mt/ha', 't/ac', 't/ha'])
  unit?: string

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(8)
  week?: number

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(255)
  leafCount?: number

  @IsOptional()
  @IsArray()
  @ValidateNested()
  @Type(() => SaveScoutingYieldStandCount)
  standCounts?: SaveScoutingYieldStandCount[]
}

export class SaveScoutingQualityDefect {
  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  item!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsNotEmpty()
  @IsInt()
  @Min(0)
  @Max(100)
  fieldPart!: number
}

// OBSERVATION
export class SaveScoutingForeignMaterial {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  type!: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(255)
  subType!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  level!: string

  @IsString()
  @IsOptional()
  @Validate(IsISO8601WithTimezone)
  targetDate?: string
}

export class SaveScoutingWeed {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  item!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  pressure!: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  stage!: string
}

export class SaveScoutingInsect {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  item!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  pressure!: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  stage!: string
}

export class SaveScoutingDisease {
  @IsNumber()
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  id?: number

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  item!: string

  @IsString()
  @IsOptional()
  @MaxLength(45)
  area?: string

  @IsString()
  @IsNotEmpty()
  @MaxLength(45)
  pressure!: string
}

export class SaveScouting extends SaveReporting {
  public static override build(raw: any) {
    return plainToInstance(SaveScouting, raw)
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @Validate(IsISO8601WithTimezone)
  @IsNotEmpty()
  date!: string

  @IsNumber()
  @IsNotEmpty()
  scoutedBy!: number

  @IsString()
  @IsNotEmpty()
  type!: string

  @IsString()
  @IsNotEmpty()
  subType!: string

  @ArrayNotEmpty()
  @IsNumber({}, { each: true })
  crops!: number[]

  @IsString()
  @IsNotEmpty()
  region!: string

  @IsNumber()
  @IsNotEmpty()
  regionId!: number

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingYield)
  yieldEstimation?: SaveScoutingYield

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingEnvironment)
  environment?: SaveScoutingEnvironment

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingFlowering)
  flowering?: SaveScoutingFlowering

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingFoodSafety)
  foodSafety?: SaveScoutingFoodSafety

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingGrowthStage)
  growthStage?: SaveScoutingGrowthStage

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingWeatherDamage)
  weatherDamage?: SaveScoutingWeatherDamage

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingQualityDefect)
  qualityDefect?: SaveScoutingQualityDefect

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingForeignMaterial)
  foreignMaterial?: SaveScoutingForeignMaterial

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingWeed)
  weed?: SaveScoutingWeed

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingInsect)
  insect?: SaveScoutingInsect

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveScoutingDisease)
  disease?: SaveScoutingDisease

  @IsString()
  @IsOptional()
  note?: string

  @IsArray()
  @ArrayMaxSize(3)
  @IsOptional()
  picturesPath?: string[]

  @IsArray()
  @IsOptional()
  @ValidateNested()
  @Type(() => SaveLocation)
  locations?: SaveLocation[]

  @IsArray()
  @IsOptional()
  @ValidateNested()
  @Type(() => SaveBoundary)
  boundaries?: SaveBoundary[]

  @IsString()
  @IsOptional()
  @Validate(IsISO8601WithTimezone)
  disablingDate?: string
}
